import { AlertTriangle, CheckCircle2, RefreshCcw } from 'lucide-react';
import { useEffect, useState } from 'react';

interface FailureRow {
  id: string;
  deliveryId: string;
  triggerKey: string;
  channel: string;
  recipient: string;
  failureClass: string;
  failureMessage: string | null;
  failedAt: string;
  acknowledgedAt: string | null;
  acknowledgedByMembershipId: string | null;
}

interface FailuresResponse {
  failures: FailureRow[];
  nextCursor: string | null;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function FailuresPage() {
  const [data, setData] = useState<FailuresResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState<'pending' | 'all'>('pending');
  const [refreshing, setRefreshing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function load() {
    setRefreshing(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (acknowledged === 'pending') params.set('acknowledged', '0');
      const res = await fetch(`/api/comm-failed-deliveries?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        setError(`Failed to load (HTTP ${res.status})`);
        return;
      }
      const body = (await res.json()) as FailuresResponse;
      setData(body);
    } catch {
      setError('Network error');
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acknowledged]);

  async function handleRetry(id: string) {
    setActionError(null);
    try {
      const res = await fetch(`/api/comm-failed-deliveries/${id}/retry`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setActionError(`Retry failed (HTTP ${res.status})`);
        return;
      }
      await load();
    } catch {
      setActionError('Network error retrying');
    }
  }

  async function handleAcknowledge(id: string) {
    setActionError(null);
    try {
      const res = await fetch(`/api/comm-failed-deliveries/${id}/acknowledge`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        setActionError(`Acknowledge failed (HTTP ${res.status})`);
        return;
      }
      await load();
    } catch {
      setActionError('Network error acknowledging');
    }
  }

  return (
    <div className="space-y-6 px-4 py-6 md:px-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Failed deliveries</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Triage failed emails, SMS, WhatsApp, and push notifications. Retry or acknowledge.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={refreshing}
          className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-secondary press disabled:opacity-50"
        >
          <RefreshCcw
            className={`size-3.5 ${refreshing ? 'animate-spin' : ''}`}
            strokeWidth={1.75}
          />
          Refresh
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {(['pending', 'all'] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setAcknowledged(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium press ${
              acknowledged === s
                ? 'bg-primary text-primary-foreground'
                : 'border border-border bg-surface text-muted-foreground hover:bg-secondary'
            }`}
          >
            {s === 'pending' ? 'Pending review' : 'All'}
          </button>
        ))}
      </div>

      {error && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      )}
      {actionError && (
        <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {actionError}
        </p>
      )}

      {!error && data === null && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data && data.failures.length === 0 && (
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <CheckCircle2 className="mx-auto size-8 text-success" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-muted-foreground">
            {acknowledged === 'pending' ? 'All caught up — no pending failures.' : 'No failures.'}
          </p>
        </div>
      )}

      {data && data.failures.length > 0 && (
        <ul className="space-y-3">
          {data.failures.map((f) => (
            <li key={f.id} className="rounded-xl bg-surface px-5 py-4 ring-1 ring-border">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" strokeWidth={1.75} />
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-mono text-xs text-foreground">{f.triggerKey}</span>
                    <span className="text-xs capitalize text-muted-foreground">{f.channel}</span>
                    <span className="num text-xs text-muted-foreground">
                      {formatDateTime(f.failedAt)}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="text-foreground">{f.recipient}</span>
                  </div>
                  <div className="text-sm">
                    <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive">
                      {f.failureClass.replace(/_/g, ' ')}
                    </span>
                    {f.failureMessage && (
                      <span className="ml-2 text-xs text-muted-foreground">{f.failureMessage}</span>
                    )}
                  </div>
                </div>
                {!f.acknowledgedAt && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleRetry(f.id)}
                      className="rounded-md border border-border px-3 py-1 text-xs hover:bg-secondary press"
                    >
                      Retry
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleAcknowledge(f.id)}
                      className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground press"
                    >
                      Acknowledge
                    </button>
                  </div>
                )}
                {f.acknowledgedAt && <span className="text-xs text-success">Acknowledged</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
